import DashboardLayout from "@/layout/dashboard-layout";
import { ReactNode, useState } from "react";
import { Head, router } from "@inertiajs/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ExternalLink,
  Save,
  Sparkles,
  Trash2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

type Article = {
  id: number;
  status: string;
  status_label: string;
  source: string | null;
  sector: string | null;
  source_url: string;
  original_title: string | null;
  original_content: string | null;
  rewritten_title: string | null;
  rewritten_content: string | null;
  error_message: string | null;
  published_at: string | null;
  post_id: number | null;
};

type Option = { id: number; name: string; domain?: string };

type Props = {
  article: Article;
  blogs: Option[];
  categories: Option[];
};

const ScrapedArticleShow = ({ article, blogs, categories }: Props) => {
  const [title, setTitle] = useState(article.rewritten_title ?? "");
  const [content, setContent] = useState(article.rewritten_content ?? "");
  const [blogId, setBlogId] = useState<string>(blogs[0]?.id?.toString() ?? "");
  const [categoryId, setCategoryId] = useState<string>(categories[0]?.id?.toString() ?? "");
  const [status, setStatus] = useState("draft");
  const [scheduledAt, setScheduledAt] = useState("");
  const [processing, setProcessing] = useState(false);

  const converted = article.status === "converted";

  const saveEdits = () => {
    router.patch(
      `/dashboard/scraped_articles/${article.id}`,
      { scraped_article: { rewritten_title: title, rewritten_content: content } },
      { preserveScroll: true }
    );
  };

  const rewriteWithAI = () => {
    setProcessing(true);
    router.post(
      `/dashboard/scraped_articles/${article.id}/rewrite`,
      {},
      { onFinish: () => setProcessing(false) }
    );
  };

  const approve = () => {
    if (!blogId || !categoryId) return;
    router.post(`/dashboard/scraped_articles/${article.id}/approve`, {
      blog_id: blogId,
      category_id: categoryId,
      status,
      scheduled_at: status === "scheduled" ? scheduledAt : null,
      title,
      content,
    });
  };

  const discard = () => {
    router.patch(`/dashboard/scraped_articles/${article.id}/discard`);
  };

  return (
    <div className="space-y-6">
      <Head title="Revisar matéria" />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => router.visit("/dashboard/scraped_articles")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Revisar matéria</h1>
            <p className="text-sm text-muted-foreground">
              {article.source ?? "Fonte desconhecida"}
              {article.sector ? ` · ${article.sector}` : ""}
              {article.published_at ? ` · ${article.published_at}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{article.status_label}</Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(article.source_url, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Original
          </Button>
        </div>
      </div>

      {article.error_message && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{article.error_message}</span>
        </div>
      )}

      {converted && (
        <div className="flex items-center gap-2 rounded-md border border-green-600/30 bg-green-600/10 p-3 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>
            Esta matéria já foi convertida em post
            {article.post_id ? ` (#${article.post_id})` : ""}.
          </span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Original */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Matéria original</CardTitle>
            <CardDescription>Conteúdo capturado da fonte (somente leitura)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <h3 className="font-semibold">{article.original_title}</h3>
            <div
              className="prose prose-sm max-h-[480px] max-w-none overflow-auto rounded-md border bg-muted/30 p-4"
              dangerouslySetInnerHTML={{ __html: article.original_content ?? "" }}
            />
          </CardContent>
        </Card>

        {/* Reescrita */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Versão reescrita</CardTitle>
              <CardDescription>Editável — texto autoral gerado pela IA</CardDescription>
            </div>
            <Button variant="outline" size="sm" isLoading={processing} onClick={rewriteWithAI}>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Reescrever com IA
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="rw-title">Título</Label>
              <Input
                id="rw-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Título reescrito"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rw-content">Conteúdo (HTML)</Label>
              <Textarea
                id="rw-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Conteúdo reescrito em HTML"
                className="min-h-[360px] font-mono text-xs"
              />
            </div>
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={saveEdits}>
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Salvar edições
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Aprovação */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Publicar como post</CardTitle>
          <CardDescription>
            Escolha o blog de destino e o status para converter esta matéria em um post.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>Blog</Label>
              <Select value={blogId} onValueChange={setBlogId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {blogs.map((b) => (
                    <SelectItem key={b.id} value={b.id.toString()}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Rascunho</SelectItem>
                  <SelectItem value="scheduled">Agendar</SelectItem>
                  <SelectItem value="published">Publicar agora</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {status === "scheduled" && (
              <div className="space-y-2">
                <Label>Data/hora</Label>
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <Button variant="ghost" className="text-destructive" onClick={discard}>
              <Trash2 className="mr-1.5 h-4 w-4" />
              Descartar
            </Button>
            <Button
              onClick={approve}
              disabled={converted || !blogId || !categoryId || (status === "scheduled" && !scheduledAt)}
            >
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
              Aprovar e criar post
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

ScrapedArticleShow.layout = (page: ReactNode) => (
  <DashboardLayout>{page}</DashboardLayout>
);

export default ScrapedArticleShow;
